import { ErrorResponse } from 'react-router-dom';
import {
  IGroupHealthInsuranceDependentsDataSource,
  IGroupHealthInsuranceDependentsValues,
  IResponse,
} from '../types';
import { RequestMethods } from '../services/enum';
import { IUseHttpServiceProps } from '../services/type';
import { IExpandedRow } from '../components/DataTable/type';
import { prepareFormData } from '../utils/functions';

export default class GroupHealthInsuranceDependentsService {
  private readonly baseUrl =
    'health-insurances/group-health-insurance-dependents/';

  constructor(
    private readonly executeService: <T>(
      args: IUseHttpServiceProps,
    ) => Promise<T | ErrorResponse | null>,
    private readonly parameters: string = '',
  ) {}

  async fetchData(): Promise<
    IResponse<IGroupHealthInsuranceDependentsDataSource> | ErrorResponse | null
  > {
    return await this.executeService({
      method: RequestMethods.GET,
      url: `${this.baseUrl}${this.parameters}`,
    });
  }

  async fetchItemById(
    id: number,
  ): Promise<IGroupHealthInsuranceDependentsDataSource | ErrorResponse | null> {
    return await this.executeService({
      method: RequestMethods.GET,
      url: `${this.baseUrl}${id}/`,
    });
  }

  async submitForm(
    values: IGroupHealthInsuranceDependentsValues,
    selectedItem?: IGroupHealthInsuranceDependentsDataSource,
  ): Promise<IGroupHealthInsuranceDependentsDataSource | ErrorResponse | null> {
    const formData =
      prepareFormData<IGroupHealthInsuranceDependentsValues>(values);

    const method = selectedItem ? RequestMethods.PATCH : RequestMethods.POST;
    const url = selectedItem
      ? `${this.baseUrl}${selectedItem.id}/`
      : this.baseUrl;

    return await this.executeService<IGroupHealthInsuranceDependentsDataSource>(
      {
        method,
        url,
        data: formData,
        contentType: 'multipart/form-data',
      },
    );
  }

  async deleteItemById(id: number): Promise<unknown | ErrorResponse | null> {
    return await this.executeService({
      method: RequestMethods.DELETE,
      url: `${this.baseUrl}${id}/`,
    });
  }

  async fetchExpandedRows(
    id: number,
  ): Promise<IExpandedRow[] | ErrorResponse | null> {
    return await this.executeService({
      method: RequestMethods.GET,
      url: `${this.baseUrl}${id}/get-latest-processes/`,
    });
  }

  async updateStatus(
    id: number,
    status: string,
    email_users: number[] = [],
  ): Promise<IGroupHealthInsuranceDependentsDataSource | ErrorResponse | null> {
    const formData = new FormData();
    formData.append('status', status);
    formData.append('email_users', email_users.join(','));

    return await this.executeService<IGroupHealthInsuranceDependentsDataSource>(
      {
        method: RequestMethods.POST,
        url: `${this.baseUrl}${id}/update-status/`,
        data: formData,
        contentType: 'multipart/form-data',
      },
    );
  }

  async uploadFiles(
    id: number,
    files: FileList,
  ): Promise<IGroupHealthInsuranceDependentsDataSource | ErrorResponse | null> {
    const formData = new FormData();
    Array.from(files).forEach((file) => formData.append('upload_files', file));

    return await this.executeService({
      method: RequestMethods.POST,
      url: `${this.baseUrl}${id}/upload-files/`,
      data: formData,
      contentType: 'multipart/form-data',
    });
  }

  async deleteFile(
    id: number,
    fileId: number,
  ): Promise<IGroupHealthInsuranceDependentsDataSource | ErrorResponse | null> {
    const formData = new FormData();
    formData.append('file_id', fileId.toString());

    return await this.executeService({
      method: RequestMethods.POST,
      url: `${this.baseUrl}${id}/delete-file/`,
      data: formData,
      contentType: 'multipart/form-data',
    });
  }

  async exportData(): Promise<{ download_url: string } | ErrorResponse | null> {
    return await this.executeService({
      method: RequestMethods.GET,
      url: `${this.baseUrl}export-excel/${this.parameters}`,
      contentType: 'multipart/form-data',
    });
  }

  async uploadMultiFiles(
    files: FileList | File[],
  ): Promise<
    IResponse<IGroupHealthInsuranceDependentsDataSource> | ErrorResponse | null
  > {
    const formData = new FormData();
    Array.from(files as File[]).forEach((file) =>
      formData.append('upload_files', file),
    );

    return await this.executeService({
      method: RequestMethods.POST,
      url: `${this.baseUrl}upload-multi-files/`,
      data: formData,
      contentType: 'multipart/form-data',
    });
  }

  async cancelDependent(
    id: number,
    end_date: string,
  ): Promise<unknown | ErrorResponse | null> {
    return await this.executeService({
      method: RequestMethods.POST,
      url: `${this.baseUrl}${id}/cancel-dependent/`,
      data: { end_date },
      contentType: 'multipart/form-data',
    });
  }

  async fetchPeopleForOrg(
    id: number,
  ): Promise<{ id: number; name: string }[] | ErrorResponse | null> {
    return await this.executeService({
      method: RequestMethods.GET,
      url: `${this.baseUrl}people-for-org/?organization=${id}`,
    });
  }
}
